import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import api from '../api/api';

const EntitlementsContext = createContext(null);

const isEntitlementMutationRequest = (config) => {
  const method = String(config?.method || 'get').toLowerCase();
  const url = String(config?.url || '');

  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false;

  return (
    url.includes('/api/workspace') ||
    url.includes('/unlock-email') ||
    url.includes('/bulk-unlock') ||
    url.includes('/bulk-adopt') ||
    url.includes('/people/enroll') ||
    (url.includes('/people/') && url.includes('/adopt')) ||
    url.includes('/api/exports/discovery') ||
    url.includes('/api/contacts/export') ||
    url.includes('/api/subscription/') ||
    url.includes('/api/sequences') ||
    url.includes('/api/workflows') ||
    url.includes('/api/billing/top-up')
  );
};

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);
  if (!context) throw new Error('useEntitlements must be used within an EntitlementsProvider');
  return context;
};

export const EntitlementsProvider = ({ children }) => {
  const { isAuthenticated, isBootstrapped } = useSelector((state) => state.auth);
  const { activeWorkspace } = useSelector((state) => state.workspace);
  const workspaceId = activeWorkspace?._id || null;

  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const requestSeqRef = useRef(0);
  const loadingRequestRef = useRef(0);

  const canFetchEntitlements = !!(isAuthenticated && isBootstrapped && workspaceId);

  const refreshEntitlements = useCallback(async ({ silent = false } = {}) => {
    if (!canFetchEntitlements) {
      setSnapshot(null);
      setError(null);
      setIsLoading(false);
      setLastSyncedAt(null);
      return null;
    }

    const requestId = requestSeqRef.current + 1;
    requestSeqRef.current = requestId;

    if (!silent) {
      loadingRequestRef.current = requestId;
      setIsLoading(true);
    }

    try {
      const res = await api.get('/api/entitlements/snapshot');
      const nextSnapshot = res?.data?.data || null;

      if (requestSeqRef.current === requestId) {
        setSnapshot(nextSnapshot);
        setError(null);
        setLastSyncedAt(new Date());
      }

      return nextSnapshot;
    } catch (err) {
      if (requestSeqRef.current === requestId) {
        setError(err);
      }
      return null;
    } finally {
      if (!silent && loadingRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [canFetchEntitlements]);

  useEffect(() => {
    refreshEntitlements();
  }, [refreshEntitlements]);

  useEffect(() => {
    if (!canFetchEntitlements) return undefined;

    const handleFocus = () => refreshEntitlements({ silent: true });
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshEntitlements({ silent: true });
    };
    const handleStorage = (event) => {
      if (event.key === 'activeWorkspace') refreshEntitlements({ silent: true });
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, [canFetchEntitlements, refreshEntitlements]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => {
        if (canFetchEntitlements && isEntitlementMutationRequest(response?.config)) {
          refreshEntitlements({ silent: true });
        }
        return response;
      },
      (err) => {
        const blockerCategory = err?.response?.data?.blockerCategory;
        if (
          canFetchEntitlements &&
          err?.response?.status === 403 &&
          ['FEATURE_LOCKED', 'LIMIT_REACHED', 'QUOTA_EXHAUSTED', 'INSUFFICIENT_CREDITS'].includes(blockerCategory)
        ) {
          refreshEntitlements({ silent: true });
        }
        return Promise.reject(err);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [canFetchEntitlements, refreshEntitlements]);

  const hasEntitlement = useCallback((featureKey) => {
    if (!snapshot?.featureAccess) return false;
    return snapshot.featureAccess[featureKey] === true;
  }, [snapshot]);

  const getLimit = useCallback((limitKey) => {
    return snapshot?.hardLimits?.[limitKey] || null;
  }, [snapshot]);

  const getQuota = useCallback((quotaKey) => {
    return snapshot?.quotas?.[quotaKey] || null;
  }, [snapshot]);

  const getRemainingCredits = useCallback((creditKey) => {
    const bucket = snapshot?.credits?.[creditKey];
    if (!bucket) return 0;
    if (bucket.remaining === Infinity || bucket.isUnlimited) return Infinity;
    const parsed = Number(bucket.remaining);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [snapshot]);

  const isLimitReached = useCallback((limitKey, usageOverride = null) => {
    const bucket = snapshot?.hardLimits?.[limitKey];
    if (!bucket) return false;
    if (bucket.limit === Infinity || bucket.isUnlimited) return false;

    if (usageOverride === null || usageOverride === undefined) {
      return !!bucket.isReached;
    }

    const usage = Number(usageOverride);
    const limit = Number(bucket.limit);
    if (!Number.isFinite(usage) || !Number.isFinite(limit)) return false;
    return usage >= limit;
  }, [snapshot]);

  const value = useMemo(() => ({
    snapshot,
    planCode: snapshot?.plan?.code || 'FREE',
    planStatus: snapshot?.plan?.status || 'ACTIVE',
    isLoading,
    error,
    lastSyncedAt,
    refreshEntitlements,
    hasEntitlement,
    getLimit,
    getQuota,
    getRemainingCredits,
    isLimitReached
  }), [
    snapshot,
    isLoading,
    error,
    lastSyncedAt,
    refreshEntitlements,
    hasEntitlement,
    getLimit,
    getQuota,
    getRemainingCredits,
    isLimitReached
  ]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};
